import { isWeb } from "@memorang/helpers/platform";
import type { UnifiedCitation } from "@memorang/types";
import Icon from "@react-native-vector-icons/material-design-icons/static";
import { useCallback, useEffect, useRef, useState } from "react";
import { Image, Pressable, TouchableOpacity, View } from "react-native";
import { Badge, Button, Portal, Text } from "react-native-paper";
import { themeRoundnessMultiplier } from "../../../constants";
import { useAppTheme } from "../../../useAppTheme";
import Box from "../../Box";
import MarkdownText from "../MarkdownText";
export type Props = {
	sources?: UnifiedCitation[] | string;
	onPressSource?: (url: string | { sourceId: number; page: number }) => void;
	onPressAllSources?: () => void;
};

const StreamedWithSource = ({
	onPressSource,
	sources: sources1,
	onPressAllSources,
}: Props) => {
	const theme = useAppTheme();
	const [hoveredSource, setHoveredSource] = useState<number | null>(null);
	const [isHoveringPopup, setIsHoveringPopup] = useState(false);
	const hoverTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const [sourcePositions, setSourcePositions] = useState<{
		[key: number]: {
			x: number;
			y: number;
			width: number;
			height: number;
		};
	}>({});
	const [pressablePositions, setPressablePositions] = useState<{
		[key: number]: {
			x: number;
			y: number;
			width: number;
			height: number;
		};
	}>({});
	const sourceRefs = useRef<{ [key: number]: View | null }>({});
	const pressableRefs = useRef<{ [key: number]: View | null }>({});
	const scrollListenerRef = useRef<(() => void) | null>(null);

	const handleSourceHoverIn = (index: number) => {
		if (hoverTimeoutRef.current) {
			clearTimeout(hoverTimeoutRef.current);
			hoverTimeoutRef.current = null;
		}
		if (isWeb && pressableRefs.current[index]) {
			pressableRefs.current[index]?.measureInWindow((x, y, width, height) => {
				setPressablePositions((prev) => ({
					...prev,
					[index]: { x, y, width, height },
				}));
			});
		}

		// Measure position using measureInWindow for Portal positioning
		if (isWeb && sourceRefs.current[index]) {
			sourceRefs.current[index]?.measureInWindow((x, y, width, height) => {
				setSourcePositions((prev) => ({
					...prev,
					[index]: { x, y, width, height },
				}));
			});
		}

		setHoveredSource(index);
		setIsHoveringPopup(false);
	};

	const handleSourceHoverOut = () => {
		if (!isHoveringPopup) {
			hoverTimeoutRef.current = setTimeout(() => {
				setHoveredSource(null);
			}, 100);
		}
	};

	const handlePopupHoverIn = () => {
		if (hoverTimeoutRef.current) {
			clearTimeout(hoverTimeoutRef.current);
			hoverTimeoutRef.current = null;
		}
		setIsHoveringPopup(true);
	};

	const handlePopupHoverOut = () => {
		setIsHoveringPopup(false);
		hoverTimeoutRef.current = setTimeout(() => {
			setHoveredSource(null);
		}, 500);
	};
	const handleSourcePress = (source: UnifiedCitation) => {
		if (source.url) {
			onPressSource?.(source.url);
		} else {
			onPressSource?.({
				sourceId: source.sourceId || 0,
				page: source.page || 0,
			});
		}
	};

	const updatePositions = useCallback(() => {
		if (hoveredSource !== null && isWeb) {
			// Update source position
			if (sourceRefs.current[hoveredSource]) {
				sourceRefs.current[hoveredSource]?.measureInWindow(
					(x, y, width, height) => {
						setSourcePositions((prev) => ({
							...prev,
							[hoveredSource]: { x, y, width, height },
						}));
					},
				);
			}
			// Update pressable position
			if (pressableRefs.current[hoveredSource]) {
				pressableRefs.current[hoveredSource]?.measureInWindow(
					(x, y, width, height) => {
						setPressablePositions((prev) => ({
							...prev,
							[hoveredSource]: { x, y, width, height },
						}));
					},
				);
			}
		}
	}, [hoveredSource]);

	// Set up scroll tracking
	useEffect(() => {
		if (isWeb && hoveredSource !== null) {
			const handleScroll = () => {
				updatePositions();
			};

			// Add scroll listeners to window and all scrollable containers
			window.addEventListener("scroll", handleScroll, true);
			window.addEventListener("resize", handleScroll);

			// Store cleanup function
			scrollListenerRef.current = () => {
				window.removeEventListener("scroll", handleScroll, true);
				window.removeEventListener("resize", handleScroll);
			};

			return () => {
				if (scrollListenerRef.current) {
					scrollListenerRef.current();
				}
			};
		}
	}, [hoveredSource, updatePositions]);
	if (typeof sources1 === "string") {
		return (
			<Box paddingVertical={8} paddingRight={16}>
				<TouchableOpacity onPress={() => onPressSource}>
					<MarkdownText
						text={sources1}
						variant="explanation"
						style={{ color: theme.colors.onSurfaceVariant }}
					/>
				</TouchableOpacity>
			</Box>
		);
	}
	if (Array.isArray(sources1) && sources1?.length > 0) {
		return (
			<Box gap={8}>
				<>
					<Box paddingVertical={8} paddingRight={16}>
						{sources1?.map((source, index) => (
							<Box key={source.id}>
								<Box flexDirection="row" gap={8}>
									<View
										ref={(ref) => {
											sourceRefs.current[index] = ref;
										}}
										style={{ flex: 1 }}
									>
										<MarkdownText
											text={source.title}
											variant="explanation"
											style={{
												color: theme.colors.onSurfaceVariant,
											}}
										/>
										<Text
											style={{
												color: theme.colors.onSurfaceVariant,
											}}
											variant="bodyMedium"
										>
											{source.pageContent}
											{isWeb ? (
												<Pressable
													ref={(ref) => {
														pressableRefs.current[index] = ref;
													}}
													onPress={() => {
														handleSourcePress(source);
													}}
													onHoverIn={() => handleSourceHoverIn(index)}
													onHoverOut={handleSourceHoverOut}
													style={{
														flexDirection: "row",
													}}
												>
													<Badge
														style={{
															marginLeft: 4,
															backgroundColor: theme.colors.secondaryContainer,
															color: theme.colors.onSecondaryContainer,
															borderRadius:
																theme.roundness * themeRoundnessMultiplier,
															fontSize: 12,
														}}
													>
														{index + 1}
													</Badge>
												</Pressable>
											) : (
												<Text
													onPress={() => handleSourcePress(source)}
													variant="labelSmall"
													style={{
														color: theme.colors.onSecondaryContainer,
														paddingVertical: 2,
														borderRadius: 4,
														paddingHorizontal: 5,
														backgroundColor: theme.colors.secondaryContainer,
													}}
												>
													{index + 1}
												</Text>
											)}
										</Text>
									</View>
								</Box>
							</Box>
						))}
					</Box>

					{hoveredSource !== null &&
						isWeb &&
						sourcePositions[hoveredSource] && (
							<Portal>
								<Box
									backgroundColor={theme.colors.surfaceVariant}
									style={{
										elevation: 3,
										shadowColor: theme.colors.shadow,
										shadowOffset: {
											width: 0,
											height: 2,
										},
										shadowOpacity: 0.25,
										shadowRadius: 3.84,
										zIndex: 9999,
									}}
									position="absolute"
									top={
										sourcePositions[hoveredSource].y +
										sourcePositions[hoveredSource].height +
										10
									}
									left={
										pressablePositions[hoveredSource].x -
											sourcePositions[hoveredSource].x >
										112
											? pressablePositions[hoveredSource].x - 112
											: pressablePositions[hoveredSource].x -
												(pressablePositions[hoveredSource].x -
													sourcePositions[hoveredSource].x)
									}
									width={224}
									borderRadius={12}
									padding={12}
									gap={4}
									onMouseEnter={handlePopupHoverIn}
									onMouseLeave={handlePopupHoverOut}
								>
									<TouchableOpacity
										onPress={() => handleSourcePress(sources1?.[hoveredSource])}
									>
										<Box>
											<Text
												variant="labelLarge"
												style={{
													color: theme.colors.onBackground,
													fontWeight: "bold",
												}}
											>
												{sources1?.[hoveredSource].title}
											</Text>
											<Text
												numberOfLines={2}
												variant="bodyMedium"
												style={{ color: theme.colors.onBackground }}
											>
												{sources1?.[hoveredSource].pageContent}
											</Text>
											<Box
												flexDirection="row"
												gap={4}
												paddingTop={8}
												alignItems="center"
											>
												{sources1?.[hoveredSource].thumbnailUri ? (
													<Image
														source={{
															uri: sources1?.[hoveredSource].thumbnailUri,
														}}
														style={{ width: 16, height: 16, borderRadius: 4 }}
													/>
												) : (
													<Icon
														name={
															sources1?.[hoveredSource].url
																? "web"
																: "book-open-page-variant-outline"
														}
														size={16}
														style={{ color: theme.colors.onSurfaceVariant }}
													/>
												)}
												<Text
													variant="labelSmall"
													style={{ color: theme.colors.onSurfaceVariant }}
													numberOfLines={1}
												>
													{sources1?.[hoveredSource].url
														? `${sources1?.[hoveredSource].url}`
														: `Page ${sources1?.[hoveredSource].page}`}
												</Text>
											</Box>
										</Box>
									</TouchableOpacity>
								</Box>
							</Portal>
						)}
					<Button
						onPress={() => onPressAllSources?.()}
						mode="outlined"
						style={{
							alignSelf: "flex-start",
							borderRadius: 100,
						}}
					>
						<Box
							flexDirection="row"
							gap={8}
							justifyContent="center"
							alignItems="center"
						>
							<Icon
								name="link-variant"
								size={16}
								style={{ color: theme.colors.primary }}
							/>
							<Text
								variant="labelLarge"
								style={{ color: theme.colors.primary }}
							>
								Sources
							</Text>
						</Box>
					</Button>
				</>
			</Box>
		);
	}
};

export default StreamedWithSource;
